import React, { useState, useEffect } from 'react';
import { Typography, Card, Spin, Progress, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import './InitialMenu.css';
import { routes } from '../routes';
import { HOME_QUOTES, HOME_QUOTES_TR } from '../data/homeQuotes';
import { PROJECT_START_DATE, PROJECT_END_DATE } from '../data/projectTimeline';
import { getDailyRandomQuote } from '../utils/homeQuote';
import dayjs from 'dayjs';
import { getWindDirTextShort, useOpenMeteoWeatherNow } from '../hooks/useOpenMeteoWeather';
import { useKitchenMenuForDate } from '../hooks/useKitchenMenu';
import PendingTasksCard from '../components/common/TaskManagement/PendingTasksCard';
import type { MealType } from '../types';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../i18n/constants';
import { useAuth } from '../components/common/security/AuthContext';
import { canActOnPage, isPathAllowedForRole } from '../utils/permissions';
import type { RouteItem } from '../routes';
import {
  LaptopOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  BuildOutlined,
  ToolOutlined,
  FolderOpenOutlined,
  HighlightOutlined,
  FileDoneOutlined,
  SettingOutlined,
  CloudOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  CoffeeOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Module icon mapping
const MODULE_ICONS: Record<string, React.ReactNode> = {
  '/core': <SettingOutlined />,
  '/it': <LaptopOutlined />,
  '/admin': <TeamOutlined />,
  '/hse': <SafetyCertificateOutlined />,
  '/qaqc': <CheckCircleOutlined />,
  '/material': <DatabaseOutlined />,
  '/to': <FileTextOutlined />,
  '/construction': <BuildOutlined />,
  '/machinery': <ToolOutlined />,
  '/dcc': <FolderOpenOutlined />,
  '/design': <HighlightOutlined />,
  '/official': <FileDoneOutlined />,
};

// Module accent colors
const MODULE_COLORS: Record<string, string> = {
  '/core': '#595959',
  '/it': '#1677ff',
  '/admin': '#722ed1',
  '/hse': '#faad14',
  '/qaqc': '#52c41a',
  '/material': '#13c2c2',
  '/to': '#eb2f96',
  '/construction': '#fa541c',
  '/machinery': '#2f54eb',
  '/dcc': '#fa8c16',
  '/design': '#a0d911',
  '/official': '#597ef7',
};

// Canlı saat kendi bileşeninde: saniyelik tik yalnızca bu bileşeni render eder,
// tüm sayfayı değil.
const LiveClock: React.FC = () => {
  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const timer = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="cc-header-clock">
      <div className="cc-live-time">{now.format('HH:mm:ss')}</div>
      <div className="cc-live-date">{now.format('ddd, DD MMM YYYY')}</div>
    </div>
  );
};

const InitialMenu: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { page_permissions, permitted_pages, role } = useAuth();
  const appLang = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  const quotePool = appLang === 'tr' && HOME_QUOTES_TR.length > 0 ? HOME_QUOTES_TR : HOME_QUOTES;
  const quote = getDailyRandomQuote(quotePool, `home-quote:daily:v1:${appLang}`);

  // Weather from Open-Meteo API (free, no key) for Kiyanly, Turkmenistan
  const { data: weather, isLoading: weatherLoading } = useOpenMeteoWeatherNow();

  // Günün menüsü: mutfak modülünün girdiği gerçek veri (vw_adm_kitch_menu_details)
  const todayStr = dayjs().format('YYYY-MM-DD');
  const { data: todayMenuRows, isLoading: menuLoading } = useKitchenMenuForDate(todayStr);
  const { lunchItems, dinnerItems } = React.useMemo(() => {
    const pick = (type: MealType) => {
      const seen = new Set<number>();
      return (todayMenuRows ?? [])
        .filter(r => r.meal_type === type && r.course_id != null && r.planned_recipe_name)
        .filter(r => (seen.has(r.course_id!) ? false : (seen.add(r.course_id!), true)))
        .map(r => ({ courseId: r.course_id!, name: r.planned_recipe_name! }));
    };
    return { lunchItems: pick('Lunch'), dinnerItems: pick('Dinner') };
  }, [todayMenuRows]);

  // Module list from routes
  const modules = React.useMemo(() => {
    return Object.entries(routes)
      .filter(([k, s]) => !['home', 'notFound', 'root', 'login', 'profile', 'formPreview', 'tasks'].includes(k) && s?.label && s?.path)
      .map(([, section]) => ({
        path: section.path,
        label: String(section.label),
        icon: MODULE_ICONS[section.path] || <DashboardOutlined />,
        color: MODULE_COLORS[section.path] || '#1677ff',
      }));
  }, []);

  // Project timeline — tarihler data/projectTimeline.ts'te; gün hassasiyeti
  // yeterli olduğundan render anındaki değer kullanılır (saniyelik tik yok).
  const now = dayjs();
  const PROJECT_START = dayjs(PROJECT_START_DATE);
  const PROJECT_END = dayjs(PROJECT_END_DATE);
  const totalDays = PROJECT_END.diff(PROJECT_START, 'day');
  const elapsedDays = now.diff(PROJECT_START, 'day');
  const progressPercent = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));

  const nowCapped = now.isAfter(PROJECT_END) ? PROJECT_END : now;
  const diffYears = PROJECT_END.diff(nowCapped, 'year');
  const afterYears = nowCapped.add(diffYears, 'year');
  const diffMonths = PROJECT_END.diff(afterYears, 'month');
  const afterMonths = afterYears.add(diffMonths, 'month');
  const diffDays = PROJECT_END.diff(afterMonths, 'day');
  const totalDaysLeft = PROJECT_END.diff(nowCapped, 'day');

  const canReadPath = React.useCallback(
    (path: string) =>
      isPathAllowedForRole(path, role) && canActOnPage(page_permissions, permitted_pages, path, 'read'),
    [page_permissions, permitted_pages, role]
  );

  const findFirstPermittedDescendantPath = React.useCallback(
    (node?: RouteItem): string | null => {
      if (!node) return null;
      if (node.element && node.path && canReadPath(node.path)) return node.path;
      const children = Object.values(node.children ?? {});
      for (const c of children) {
        const r = findFirstPermittedDescendantPath(c);
        if (r) return r;
      }
      const grands = Object.values(node.grandchildren ?? {});
      for (const g of grands) {
        const r = findFirstPermittedDescendantPath(g);
        if (r) return r;
      }
      return null;
    },
    [canReadPath]
  );

  const handleModuleCardClick = React.useCallback(
    (moduleBasePath: string) => {
      const section = Object.values(routes).find((r) => r?.path === moduleBasePath);
      const dashboardPath = `${moduleBasePath}/dashboard`;

      // Preferred default: module dashboard page
      if (canReadPath(dashboardPath)) {
        navigate(dashboardPath);
        return;
      }

      // Fallback: first permitted descendant page in this module
      const firstPermitted = findFirstPermittedDescendantPath(section);
      if (firstPermitted) {
        navigate(firstPermitted);
        return;
      }

      // Last resort: Home
      navigate('/home');
    },
    [canReadPath, findFirstPermittedDescendantPath, navigate]
  );

  return (
    <div className="initial-menu-layout">
      <div className="dashboard-content">
        {/* Header */}
        <div className="cc-header">
          <div className="cc-header-text">
            <Title level={3} style={{ margin: 0, color: '#fff' }}>{t('home.appTitle')}</Title>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Kiyanly, Turkmenistan</Text>
          </div>
          <LiveClock />
        </div>

        {/* 3-Column Grid */}
        <div className="cc-grid">
          {/* Left Column - Weather */}
          <div className="cc-col-left">
            <Card className="cc-weather-card" bordered={false}>
              <div className="cc-weather-header">
                <CloudOutlined style={{ fontSize: 20, marginRight: 8 }} />
                <span className="cc-card-title">{t('home.weatherNow')}</span>
              </div>
              {weatherLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
              ) : weather ? (
                <div className="cc-weather-body">
                  <div className="cc-weather-temp">
                    <span className="cc-temp-main">{weather.tempCurrent != null ? `${Math.round(weather.tempCurrent)}` : '--'}</span>
                    <span className="cc-temp-unit">°C</span>
                  </div>
                  <div className="cc-temp-range">
                    H: {weather.tempMax != null ? `${Math.round(weather.tempMax)}°` : '--'} / L: {weather.tempMin != null ? `${Math.round(weather.tempMin)}°` : '--'}
                  </div>
                  <div className="cc-weather-details">
                    <div className="cc-weather-row">
                      <span className="cc-weather-label">{t('home.wind')}</span>
                      <span className="cc-weather-value">{weather.windSpeed != null ? `${Math.round(weather.windSpeed)}` : '--'} km/h {getWindDirTextShort(weather.windDir)}</span>
                    </div>
                    <div className="cc-weather-row">
                      <span className="cc-weather-label">{t('home.humidity')}</span>
                      <span className="cc-weather-value">{weather.humidity != null ? `${Math.round(weather.humidity)}%` : '--'}</span>
                    </div>
                    <div className="cc-weather-row">
                      <span className="cc-weather-label">{t('home.pressure')}</span>
                      <span className="cc-weather-value">{weather.pressure != null ? `${Math.round(weather.pressure)} hPa` : '--'}</span>
                    </div>
                    <div className="cc-weather-row">
                      <span className="cc-weather-label">{t('home.precipitation')}</span>
                      <span className="cc-weather-value">{weather.precip != null ? `${weather.precip} mm` : '--'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('home.noWeatherData')}</div>
              )}
            </Card>

            <Card className="cc-menu-card" bordered={false}>
              <div className="cc-menu-header">
                <CoffeeOutlined style={{ fontSize: 20, marginRight: 8 }} />
                <span className="cc-card-title">{t('home.todaysMenu')}</span>
              </div>
              {menuLoading ? (
                <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>
              ) : lunchItems.length === 0 && dinnerItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 12, color: '#999' }}>{t('home.noMenuToday')}</div>
              ) : (
                <div className="cc-menu-grid">
                  {[
                    { title: t('home.lunch'), items: lunchItems },
                    { title: t('home.dinner'), items: dinnerItems },
                  ].map(section => (
                    <div key={section.title} className="cc-menu-section">
                      <div className="cc-menu-section-title">{section.title}</div>
                      <ul className="cc-menu-list">
                        {section.items.length > 0 ? (
                          section.items.map(it => (
                            <li key={it.courseId} className="cc-menu-item">{it.name}</li>
                          ))
                        ) : (
                          <li className="cc-menu-item" style={{ listStyle: 'none', color: '#bfbfbf' }}>—</li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Center Column - Module Cards */}
          <div className="cc-col-center">
            {/* Modül kartlarının üst hizasını Weather Now alt çizgisine oturtmak için spacer */}
            <div className="cc-modules-header-spacer" aria-hidden="true" />
            <div className="cc-modules-grid">
              {modules.map(mod => (
                <Tooltip key={mod.path} title={t('home.openModule', { module: mod.label })}>
                  <div
                    className="cc-module-card"
                    style={{ '--module-color': mod.color } as React.CSSProperties}
                    onClick={() => handleModuleCardClick(mod.path)}
                  >
                    <div className="cc-module-icon" style={{ color: mod.color }}>
                      {mod.icon}
                    </div>
                    <div className="cc-module-label">{mod.label}</div>
                  </div>
                </Tooltip>
              ))}
            </div>
            <div className="cc-home-quote-wrap">
              <div className="cc-home-quote">
                <Text italic className="cc-home-quote-text">
                  {quote ? `"${quote}"` : ''}
                </Text>
              </div>
            </div>
          </div>

          {/* Right Column - Project Timeline */}
          <div className="cc-col-right">
            <Card className="cc-timeline-card" bordered={false}>
              <div className="cc-timeline-header">
                <FieldTimeOutlined style={{ fontSize: 20, marginRight: 8 }} />
                <span className="cc-card-title">{t('home.projectTimeline')}</span>
              </div>

              <div className="cc-timeline-dates">
                <div className="cc-date-block">
                  <span className="cc-date-label">{t('home.start')}</span>
                  <span className="cc-date-value">{PROJECT_START.format('DD MMM YYYY')}</span>
                </div>
                <div className="cc-date-divider">→</div>
                <div className="cc-date-block">
                  <span className="cc-date-label">{t('home.end')}</span>
                  <span className="cc-date-value">{PROJECT_END.format('DD MMM YYYY')}</span>
                </div>
              </div>

              <div className="cc-progress-section">
                <div className="cc-progress-label">
                  <span>{t('home.elapsed')}</span>
                  <span>{progressPercent}%</span>
                </div>
                <Progress
                  percent={progressPercent}
                  showInfo={false}
                  strokeColor={{ from: '#1677ff', to: '#52c41a' }}
                  trailColor="#f0f0f0"
                  size="small"
                />
              </div>

              <div className="cc-countdown">
                <div className="cc-countdown-title">{t('home.timeRemaining')}</div>
                <div className="cc-countdown-grid">
                  <div className="cc-countdown-item">
                    <span className="cc-countdown-num">{diffYears}</span>
                    <span className="cc-countdown-unit">{t('home.years')}</span>
                  </div>
                  <div className="cc-countdown-item">
                    <span className="cc-countdown-num">{diffMonths}</span>
                    <span className="cc-countdown-unit">{t('home.months')}</span>
                  </div>
                  <div className="cc-countdown-item">
                    <span className="cc-countdown-num">{diffDays}</span>
                    <span className="cc-countdown-unit">{t('home.days')}</span>
                  </div>
                </div>
                <div className="cc-total-days">
                  {t('home.daysRemaining', { count: totalDaysLeft })}
                </div>
              </div>
            </Card>

            <PendingTasksCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialMenu;
